/**
 * fallbackRoutes.ts — mapeo determinístico keyword → página del dashboard,
 * usado por el fallback gracioso del asistente en ChatPage.tsx.
 *
 * Duplica (no importa) el mapa equivalente de aws-serverless/src/config/fallback-routes.ts
 * — dashboard y backend no comparten build, así que cada lado mantiene su propia copia.
 * Determinístico a propósito: nunca se delega al LLM la elección del destino.
 */

export type FallbackDestinationType = 'web_page' | 'bot_command';

export interface FallbackRoute {
  keywords: string[];
  destination: {
    type: FallbackDestinationType;
    /** Etiqueta en español (respaldo). Si hay `labelKey`, la UI usa `t.chat.fallbackLabels[labelKey]` para respetar el idioma activo. */
    label: string;
    labelKey?: 'reports' | 'sales' | 'logistics' | 'traceability' | 'devices' | 'permissions' | 'inventory' | 'tasks';
    /** Para 'web_page': ruta relativa dentro del dashboard, sin el prefijo :tenantId (ej. "/reportes") */
    webPath?: string;
    /** Para 'bot_command': el comando a sugerir por Telegram (ej. "/nuevo_gasto") */
    command?: string;
  };
}

export const FALLBACK_ROUTES: FallbackRoute[] = [
  { keywords: ['reporte', 'reportes', 'reporte pdf'], destination: { type: 'web_page', label: 'Reportes', labelKey: 'reports', webPath: '/reportes' } },
  { keywords: ['factura', 'facturas', 'cobranza', 'cartera vencida'], destination: { type: 'web_page', label: 'Ventas y Precios', labelKey: 'sales', webPath: '/ventas' } },
  { keywords: ['exportacion', 'exportaciones', 'contenedor', 'embarque'], destination: { type: 'web_page', label: 'Logística', labelKey: 'logistics', webPath: '/logistica' } },
  { keywords: ['trazabilidad', 'traza', 'de donde salio esta caja'], destination: { type: 'web_page', label: 'Trazabilidad', labelKey: 'traceability', webPath: '/trazabilidad' } },
  { keywords: ['dispositivo', 'dispositivos', 'sensor', 'gps', 'termografo'], destination: { type: 'web_page', label: 'Dispositivos', labelKey: 'devices', webPath: '/dispositivos' } },
  { keywords: ['permiso', 'permisos', 'quien puede ver'], destination: { type: 'web_page', label: 'Matriz de Permisos', labelKey: 'permissions', webPath: '/permisos' } },
  { keywords: ['inventario', 'inventarios', 'stock', 'bodega'], destination: { type: 'web_page', label: 'Inventario', labelKey: 'inventory', webPath: '/inventario' } },
  { keywords: ['tarea', 'tareas', 'pendientes'], destination: { type: 'web_page', label: 'Tareas', labelKey: 'tasks', webPath: '/tareas' } },
  { keywords: ['gasto', 'gastos', 'gasto indirecto'], destination: { type: 'bot_command', label: 'Gastos', command: '/nuevo_gasto' } },
  { keywords: ['quimico', 'quimicos', 'agroquimico', 'fumigar', 'fumigada', 'fitosanitario'], destination: { type: 'bot_command', label: 'Alta de químico', command: '/nuevo_quimico' } },
  { keywords: ['protocolo', 'protocolos', 'receta de cultivo'], destination: { type: 'bot_command', label: 'Protocolos', command: '/crear_protocolo' } },
  { keywords: ['nomina', 'nominas', 'cerrar nomina', 'pago quincenal'], destination: { type: 'bot_command', label: 'Nómina', command: '/cerrar_nomina' } },
];

/** Frases con las que el modelo declara (regla 2 del system prompt de chat.handler.ts) que no tiene datos suficientes. */
export const UNCERTAIN_REPLY_PHRASES = [
  'no tengo datos suficientes',
  'no cuento con informacion',
  'no tengo informacion',
  'no puedo ayudarte con eso',
  'no tengo esa informacion',
  'no dispongo de esa informacion',
];
